import { useEffect, useState } from "react";
import { BusyLabel } from "@/components/loading";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Mail, ArrowLeft, RefreshCw } from "lucide-react";

export default function ForgotPassword() {
  const [step, setStep] = useState("email"); // "email" | "reset"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const requestCode = async (e) => {
    if (e) e.preventDefault();
    if (!email) { toast.error("Ingresa tu correo"); return; }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      toast.success("Si el correo existe, enviamos un código.");
      setStep("reset");
      setResendCooldown(30);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar el código");
    } finally { setLoading(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error("El código tiene 6 dígitos"); return; }
    if (newPassword.length < 8) { toast.error("La contraseña debe tener al menos 8 caracteres"); return; }
    if (newPassword !== confirmPassword) { toast.error("Las contraseñas no coinciden"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        email: email.trim().toLowerCase(),
        code: code.trim(),
        new_password: newPassword,
      });
      toast.success("Contraseña actualizada. Inicia sesión con la nueva.");
      nav("/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo restablecer la contraseña");
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (resendCooldown > 0) return;
    await requestCode();
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="flex justify-center mb-8"><Logo /></div>
      <div className="bg-[#111] border border-[#262626] rounded-md p-8">
        <button
          type="button"
          onClick={() => step === "reset" ? setStep("email") : nav("/login")}
          data-testid="fp-back"
          className="inline-flex items-center gap-1 text-xs text-neutral-400 hover:text-white mb-4"
        >
          <ArrowLeft size={14} /> Volver
        </button>

        {step === "email" ? (
          <>
            <h1 className="font-display text-3xl mb-2">¿OLVIDASTE TU CONTRASEÑA?</h1>
            <p className="text-neutral-400 text-sm mb-6">Ingresa tu correo y te enviaremos un código de 6 dígitos para restablecerla.</p>
            <form onSubmit={requestCode} className="space-y-4">
              <div>
                <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Email</Label>
                <Input data-testid="fp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-[#171717] border-[#262626] text-white" />
              </div>
              <Button data-testid="fp-request" type="submit" disabled={loading} className="w-full bg-[#EF4444] hover:bg-[#DC2626]">
                <Mail size={16} className="mr-2" />
                <BusyLabel busy={loading} text="Enviando código…">Enviarme el código</BusyLabel>
              </Button>
            </form>
          </>
        ) : (
          <>
            <h1 className="font-display text-3xl mb-2">RESTABLECER</h1>
            <p className="text-neutral-400 text-sm mb-6">Revisamos <span className="text-white">{email}</span>. Ingresa el código y tu nueva contraseña.</p>
            <form onSubmit={submitReset} className="space-y-4">
              <div>
                <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Código de 6 dígitos</Label>
                <Input
                  data-testid="fp-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  className="bg-[#171717] border-[#262626] text-white text-center font-mono text-2xl tracking-[0.5em]"
                />
              </div>
              <div>
                <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Nueva contraseña</Label>
                <Input data-testid="fp-new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8} className="bg-[#171717] border-[#262626] text-white" />
              </div>
              <div>
                <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Confirmar contraseña</Label>
                <Input data-testid="fp-confirm-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8} className="bg-[#171717] border-[#262626] text-white" />
              </div>
              <Button data-testid="fp-submit" type="submit" disabled={loading} className="w-full bg-[#EF4444] hover:bg-[#DC2626]">
                <BusyLabel busy={loading} text="Actualizando…">Restablecer contraseña</BusyLabel>
              </Button>
              <button
                type="button"
                onClick={resend}
                disabled={resendCooldown > 0 || loading}
                data-testid="fp-resend"
                className="w-full text-sm text-neutral-400 hover:text-[#EF4444] disabled:opacity-40 inline-flex items-center justify-center gap-1"
              >
                <RefreshCw size={12} />
                {resendCooldown > 0 ? `Reenviar código en ${resendCooldown}s` : "Reenviar código"}
              </button>
            </form>
          </>
        )}

        <div className="mt-6 pt-6 border-t border-[#262626] text-sm text-neutral-400 text-center">
          <Link to="/login" className="text-[#EF4444] hover:underline">Volver al inicio de sesión</Link>
        </div>
      </div>
    </div>
  );
}
